import PropTypes from "prop-types";
import { getStepStates, getProgressPercent } from "./statusFlow";

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="m5 12.5 4.5 4.5L19 7.5"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const STEP_ICONS = {
  confirmed: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 12.5 11 14.5 15.5 10M12 3l7 3v5.5c0 4.2-2.9 7.9-7 9-4.1-1.1-7-4.8-7-9V6z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  processing: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 7v5l3 2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="12"
        r="8"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  ),
  dispatched: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M3 7h10v9H3zM13 10h4l3 3.5V16h-7z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="18" r="1.8" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17" cy="18" r="1.8" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  ),
  delivered: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 8.5 12 4l8 4.5v7L12 20l-8-4.5z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path
        d="m8.5 11 3 1.7 3-1.7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};

/*
 * Horizontal progress rail on desktop, vertical on narrow screens (the
 * layout switch lives in CSS so the markup stays one tree for both).
 */
const StatusStepper = ({ status }) => {
  const steps = getStepStates(status);
  const percent = getProgressPercent(status);
  const currentLabel =
    steps.find((step) => step.current)?.label || "Awaiting confirmation";

  return (
    <section className="c4trk__card" aria-labelledby="c4trk-progress-heading">
      <h2 className="c4trk__card-title" id="c4trk-progress-heading">
        Shipment Progress
      </h2>

      <p className="c4trk__sr-status" role="status">
        Current stage: {currentLabel}
      </p>

      <ol className="c4trk__steps">
        <span className="c4trk__rail" aria-hidden="true">
          <span
            className="c4trk__rail-fill"
            style={{ "--c4trk-progress": `${percent}%` }}
          />
        </span>

        {steps.map((step) => {
          const state = step.current ? "current" : step.pending ? "pending" : "done";
          return (
            <li
              className={`c4trk__step c4trk__step--${state}`}
              key={step.key}
              aria-current={step.current ? "step" : undefined}
            >
              <span className="c4trk__dot" aria-hidden="true">
                {state === "done" ? <CheckIcon /> : STEP_ICONS[step.key]}
              </span>
              <span className="c4trk__step-body">
                <span className="c4trk__step-label">{step.label}</span>
                <span className="c4trk__step-hint">
                  {step.current ? "In progress" : step.hint}
                </span>
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

StatusStepper.propTypes = {
  status: PropTypes.string,
};

export default StatusStepper;
